import { useState, type PropsWithChildren, type SyntheticEvent } from "react";
import type { FormikErrors, FormikTouched, FormikValues } from "formik";
import Typography from "@mui/material/Typography";
import { LocalizationProvider } from '@mui/x-date-pickers-v.6/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-v.6/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers-v.6/DatePicker';
import TextField from "@mui/material/TextField";
import { DateTimeFormatRepository } from "consts/general";
import {
    getFormInputProps as getFormInputPropsHelper,
    isFormChanged as isFormChangedHelper,
    type PickFormState
} from "util/forms";
import { adultDate } from "util/support";
import { useDownloadFile } from "hooks";
import { downloadLocalFile } from "features/general/api";
import { selectOnboardingLegalForms } from "features/general/selectors";
import Tabs from "ui/atoms/Tabs";
import { DownloadFile } from "ui/forms/formik-elements";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import { ReviewableInput } from "ui/widgets/Input";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { OnboardingForm, OnboardingReviewableFormInput } from "ui/forms/Onboarding/shared";
import { FormField, type OnboardingIdentityVerificationProps } from "./types";

const UBOIdentityVerification = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    initialValues,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    children,
    ...form
}: PropsWithChildren<OnboardingIdentityVerificationProps>) => {
    const [tab, setTab] = useState('1');

    const { download } = useDownloadFile(downloadLocalFile);

    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    const isFormChanged = ({ dirty, touched }: PickFormState<'touched' | 'dirty'>) => {
        const isRootChanged = isFormChangedHelper({ dirty, touched });
        let uboIdentitiesChanged = false;

        if (Array.isArray(touched.uboIdentities)) {
            uboIdentitiesChanged = touched.uboIdentities
                .some(touched => isFormChangedHelper({ dirty, touched }));
        }

        return isRootChanged || uboIdentitiesChanged;
    };

    const getFormInputProps = (fieldName: string, index: number) => (
        getFormInputPropsHelper(fieldName, {
            errors: form.errors.uboIdentities?.[index] as FormikErrors<FormikValues> ?? {},
            touched: form.touched.uboIdentities?.[index] as FormikTouched<FormikValues> ?? {}
        })
    );

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            handleSubmit={handleSubmit}
            isFormChanged={isFormChanged}
        >
            <Tabs
                value={tab}
                onChange={setTab}
                TabPanelProps={{
                    sx: {
                        display: 'grid',
                        gap: 2
                    }
                }}
                model={values.uboIdentities.map((identity, index) => ({
                    key: index,
                    label: identity.fullName || 'UBO Identity',
                    component: (
                        <>
                            <OnboardingReviewableFormInput
                                name={`${identity.pki}.${FormField.FullName}`}
                                values={values}
                                renderLabel={() => 'Full Name'}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                                    <TextField
                                        {...restFormInput}
                                        {...getFormInputProps(FormField.FullName, index)}
                                        fullWidth
                                        required
                                        size='small'
                                        name={`uboIdentities[${index}].${FormField.FullName}`}
                                        value={identity[FormField.FullName]}
                                        label='Full Name'
                                        onChange={event => setFieldValue(`uboIdentities[${index}].${FormField.FullName}`, event.target.value)}
                                        onBlur={() => setFieldTouched(`uboIdentities[${index}].${FormField.FullName}`, true, true)}
                                        InputProps={{
                                            endAdornment: reviewableStatus
                                        }}
                                    />
                                )}
                            />
                            <OnboardingReviewableFormInput
                                name={`${identity.pki}.${FormField.DateOfBirth}`}
                                values={values}
                                renderLabel={() => 'Date of Birth'}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                                    <LocalizationProvider
                                        dateAdapter={AdapterMoment}
                                    >
                                        <DatePicker
                                            disableFuture
                                            disabled={disabled}
                                            maxDate={adultDate()}
                                            format={DateTimeFormatRepository.Default}
                                            label='Date of Birth'
                                            slotProps={{
                                                textField: {
                                                    ...getFormInputProps(FormField.DateOfBirth, index),
                                                    inputRef,
                                                    name: `uboIdentities[${index}].${FormField.DateOfBirth}`,
                                                    size: 'small',
                                                    required: true,
                                                    fullWidth: true,
                                                    onBlur: () => setFieldTouched(`uboIdentities[${index}].${FormField.DateOfBirth}`, true, true),
                                                    InputProps: {
                                                        endAdornment: reviewableStatus
                                                    }
                                                }
                                            }}
                                            value={identity[FormField.DateOfBirth]}
                                            onChange={value => {
                                                setFieldValue(`uboIdentities[${index}].${FormField.DateOfBirth}`, value);
                                                setTimeout(() => setFieldTouched(`uboIdentities[${index}].${FormField.DateOfBirth}`, true, true));
                                            }}
                                        />
                                    </LocalizationProvider>
                                )}
                            />
                            <OnboardingReviewableFormInput
                                name={`${identity.pki}.${FormField.Nationality}`}
                                values={values}
                                renderLabel={() => 'Nationality'}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                                    <CountryIso2
                                        fullWidth
                                        disableClearable
                                        disabled={disabled}
                                        value={identity[FormField.Nationality] ?? ''}
                                        onChange={onAutocompleteChange(`uboIdentities[${index}].${FormField.Nationality}`)}
                                        TextFieldProps={{
                                            ...getFormInputProps(FormField.Nationality, index),
                                            inputRef,
                                            required: true,
                                            name: `uboIdentities[${index}].${FormField.Nationality}`,
                                            label: 'Nationality',
                                            InputProps: {
                                                endAdornment: reviewableStatus
                                            }
                                        }}
                                    />
                                )}
                            />
                            <OnboardingReviewableFormInput
                                name={`${identity.pki}.${FormField.ResidentialAddress}`}
                                values={values}
                                renderLabel={() => 'Residential Address'}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                                    <TextField
                                        {...restFormInput}
                                        {...getFormInputProps(FormField.ResidentialAddress, index)}
                                        required
                                        multiline
                                        fullWidth
                                        rows={4}
                                        size='small'
                                        name={`uboIdentities[${index}].${FormField.ResidentialAddress}`}
                                        value={identity[FormField.ResidentialAddress]}
                                        label='Residential Address'
                                        onChange={event => setFieldValue(`uboIdentities[${index}].${FormField.ResidentialAddress}`, event.target.value)}
                                        onBlur={() => setFieldTouched(`uboIdentities[${index}].${FormField.ResidentialAddress}`, true, true)}
                                        InputProps={{
                                            endAdornment: reviewableStatus
                                        }}
                                    />
                                )}
                            />
                            <OnboardingReviewableFormInput
                                name={`${identity.pki}.${FormField.LegalForm}`}
                                values={values}
                                renderLabel={() => 'Legal Form'}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                renderFormInput={({ disabled, reviewableStatus }) => (
                                    <StatefulSelector
                                        fullWidth
                                        disablePortal
                                        disabled={disabled}
                                        size='small'
                                        layout={null}
                                        loading={isBootstrapDataLoading}
                                        selectOptions={selectOnboardingLegalForms}
                                        value={identity[FormField.LegalForm]}
                                        onChange={onAutocompleteChange(FormField.LegalForm)}
                                        TextFieldProps={{
                                            ...getFormInputProps(FormField.LegalForm, index),
                                            label: 'Legal Form',
                                            required: true,
                                            name: FormField.LegalForm,
                                            InputProps: {
                                                endAdornment: reviewableStatus
                                            }
                                        }}
                                    />
                                )}
                            />
                            <Typography
                                marginTop={2}
                                variant='h6'
                            >
                                Proof of Identity
                            </Typography>
                            <Typography
                                variant='caption'
                                gutterBottom
                            >
                                Passport or Government-Issued ID
                            </Typography>
                            <OnboardingReviewableFormInput
                                name={`${identity.pki}.${FormField.IdentityFileKey}`}
                                values={values}
                                renderLabel={() => 'Proof of Identity'}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                slots={{
                                    actions: (
                                        <>
                                            <ReviewableInput.ApproveAction />
                                            <ReviewableInput.RejectAction />
                                            <ReviewableInput.CommentAction />
                                        </>
                                    )
                                }}
                                renderFormInput={({ reviewableStatus }) => (
                                    <DownloadFile
                                        downloadFile={() => download({
                                            fileKey: String(identity[FormField.IdentityFileKey])
                                        })}
                                        file={String(identity[FormField.IdentityFileKey])}
                                    >
                                        {reviewableStatus}
                                    </DownloadFile>
                                )}
                            />
                            <Typography
                                marginTop={2}
                                variant='h6'
                            >
                                Proof of Address
                            </Typography>
                            <Typography
                                variant='caption'
                                gutterBottom
                            >
                                Recent utility bill or official correspondence (dated within the last 3 months).
                            </Typography>
                            <OnboardingReviewableFormInput
                                name={`${identity.pki}.${FormField.AddressFileKey}`}
                                values={values}
                                renderLabel={() => 'Proof of Address'}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                slots={{
                                    actions: (
                                        <>
                                            <ReviewableInput.ApproveAction />
                                            <ReviewableInput.RejectAction />
                                            <ReviewableInput.CommentAction />
                                        </>
                                    )
                                }}
                                renderFormInput={({ reviewableStatus }) => (
                                    <DownloadFile
                                        downloadFile={() => download({
                                            fileKey: String(identity[FormField.AddressFileKey])
                                        })}
                                        file={String(identity[FormField.AddressFileKey])}
                                    >
                                        {reviewableStatus}
                                    </DownloadFile>
                                )}
                            />
                        </>
                    )
                }))}
            />
        </OnboardingForm>
    );
};

export default UBOIdentityVerification;
